import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import t from '../../../../translations/main'
import tokenUtils from '../../../../../authConfig/utils'
import { SendRequestType, Timeout, RequestConfig, ApplyDataType, OnErroraType } from './types'
import { ScopeKeys } from '../../../../../authConfig/types'
import { BodyCache } from './BodyCache'
import { callModal } from './utils'

const timeoutDict: { [key: string]: Timeout } = {}
// eslint-disable-next-line
function debounce(callback: any, delay: number, id: string) {
  clearTimeout(timeoutDict?.[id as string])
  // eslint-disable-next-line
  const params: any = arguments
  timeoutDict[id as string] = setTimeout(() => callback(...params), delay)
}

const useFetch = () => {
  const { instance, accounts } = useMsal()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const controller = new AbortController()

  const handleGetTokenError = () => {
    if (!window.location.pathname.includes('/error')) {
      navigate('/error/tokenExpired')
    }
  }

  const request: SendRequestType = async (
    requestConfig: RequestConfig,
    applyData: ApplyDataType,
    onError?: OnErroraType
  ) => {
    setIsLoading(true)
    setError(null)

    const baseHeaders = {
      ...(requestConfig?.headers || {}),
      'Authorization': requestConfig?.token ? `Bearer ${requestConfig.token}` : null,
    }

    try {
      const signal = controller.signal
      const response = await fetch(requestConfig?.url, {
        method: requestConfig?.method || 'GET',
        // @ts-ignore:next-line
        body: requestConfig?.body
          ? requestConfig.noStringfyBody
            ? requestConfig.body
            : JSON.stringify(requestConfig.body)
          : null,
        headers: requestConfig.noStringfyBody
          ? baseHeaders
          : {
            ...baseHeaders,
            'Content-Type': requestConfig?.headers?.['Content-Type'] || 'application/json',
          },
        signal: requestConfig?.signal || signal,
      })

      const bodyCache = new BodyCache(response)

      // checking if this is a return from Virtuoso field validation that contains error 
      // @ts-ignore
      const responseFromServerValidationError = response?.status === 422 && response.headers?.get('content-type').includes('json') && await bodyCache.json()
      const isResponseValid = responseFromServerValidationError?.statusCode === 204
                              && typeof responseFromServerValidationError.reasonPhrase === 'string'
                              && responseFromServerValidationError.reasonPhrase.length > 0
      if (!response?.ok && !isResponseValid && requestConfig?.scope !== 'main') {
        if (onError) onError(response?.status, bodyCache)
        if (response?.status) navigate(`/error/${response.status}`)
        throw new Error(t(`error.${response?.status}`))
      }

      const data = response?.headers?.get('content-type') && response.headers.get('content-type') !== 'text/html'
        ? response.headers.get('content-type')?.includes('text/plain')
          ? await bodyCache.text()
          : responseFromServerValidationError || await bodyCache?.[requestConfig?.resType || 'json']()
        : ''
      if(!response?.ok && requestConfig?.scope === 'main') {
        if (!requestConfig.suppressErrorModal) {
          callModal(response)
        }
        setIsLoading(false)
        if(onError) onError(response?.status, bodyCache)
        return
      }
      applyData(data, response)

    // eslint-disable-next-line
    } catch(error: any) {
      setError(error?.message || 'Something went wrong!')
    }

    setIsLoading(false)
  }

  const getToken = async (scope: ScopeKeys) => {
    if (!scope) return null

    const token = tokenUtils?.getTokenInitialState(scope)
    if (token) return token

    const result = await tokenUtils?.getToken(
      scope,
      instance,
      accounts?.[0],
      () => undefined,
      handleGetTokenError
    )

    return result ? result.accessToken : null
  }

  const sendRequest: SendRequestType = useCallback(async (
    requestConfig: RequestConfig,
    applyData: ApplyDataType,
    onError?: OnErroraType
  ) => {
    const token = await getToken(requestConfig?.scope)

    debounce(
      () => request(
        { token, ...requestConfig },
        applyData,
        onError
      ),
      requestConfig?.delay || 300,
      requestConfig?.url?.split('?')?.[0]
    )
  }, [])

  return {
    isLoading,
    error,
    sendRequest,
    controller,
  }
}

export default useFetch
